// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { GroupSecurity } from '@quromedical/fhir-common';
import type {
  ClaimResponseGetAllParams,
  ClaimResponseModel,
  ClaimResponseModelGetAllResponse,
  ClaimResponseModelGetResponse,
  ClaimResponseOutcome,
  ClaimResponseStatus,
  WithId,
} from '@quromedical/models';
import { CrudForm } from 'components/form';
import { ConfirmationButton } from 'components/input';
import type { ColumnType } from 'components/table';
import type { Field, SelectOption } from 'components/types';
import { WithGroup } from 'core/auth';
import { claimResponseTypeDisplay, getDisplayDate } from 'core/display';
import { PaginatedTable } from 'core/table';
import { Section, Text } from 'design-system';
import { router } from 'expo-router';
import { logger } from 'helpers';
import type { FetcherOptions } from 'hooks/usePaginatedData';
import { useRevalidation } from 'hooks/useRevalidation';
import { ClaimResponseApi } from 'integration/aggregate';
import type React from 'react';
import { useCallback, useState } from 'react';
import { ScrollView } from 'react-native';
import { strings } from 'strings';

import { ClaimListToggle } from './internal';

const api = new ClaimResponseApi();

interface ClaimReversalButtonProps {
  claimResponse: ClaimResponseModel;
}

const ClaimReversalButton: React.FC<ClaimReversalButtonProps> = ({
  claimResponse,
}) => {
  const revalidate = useRevalidation('ClaimResponse');

  const onSubmit = useCallback(async () => {
    await api.reverseClaim(claimResponse);
    revalidate().catch(logger.error);
  }, [claimResponse, revalidate]);

  return (
    <ConfirmationButton
      status="status-critical"
      title={strings.ClaimReversalConfirmationButtonTitle}
      message={strings.ClaimReversalConfirmationButtonMessage}
      onSubmit={onSubmit}
      buttonText={strings.ClaimReversalConfirmationButtonText}
    />
  );
};

const columns: ColumnType<WithId<ClaimResponseModelGetResponse>>[] = [
  {
    accessor: 'patient',
    header: strings.LabelNameAndSurname,
    Cell: ({ value }) => (
      <Text>{value?.display || strings.DisplayNotFound}</Text>
    ),
  } as ColumnType<ClaimResponseModel, 'patient'>,
  {
    accessor: 'type',
    header: strings.AdmissionServiceLabel,
    Cell: ({ value }) => (
      <Text>{claimResponseTypeDisplay[value] || value}</Text>
    ),
  } as ColumnType<ClaimResponseModel, 'type'>,
  {
    accessor: 'status',
    header: strings.LabelStatus,
  },
  {
    accessor: 'outcome',
    header: strings.ClaimResponseOutcome,
  },
  {
    accessor: 'date',
    header: strings.ClaimResponseLabelDate,
    Cell: ({ value }) => (
      <Text>{getDisplayDate(value) || strings.DisplayNotFound}</Text>
    ),
  } as ColumnType<ClaimResponseModel, 'date'>,
  {
    accessor: 'claim',
    header: '',
    Cell: (value) =>
      value.row.original.status !== 'active' ? (
        <></>
      ) : (
        <WithGroup groups={[GroupSecurity.ClaimResponseUpdate]}>
          <ClaimReversalButton claimResponse={value.row.original} />
        </WithGroup>
      ),
  } as ColumnType<ClaimResponseModel, 'claim'>,
];

type SearchParams = Omit<ClaimResponseGetAllParams, 'pageToken' | 'count'>;

const statuses: Zod.infer<typeof ClaimResponseStatus>[] = [
  'active',
  'draft',
  'cancelled',
  'entered-in-error',
];
const outcomes: Zod.infer<typeof ClaimResponseOutcome>[] = [
  'queued',
  'complete',
  'error',
  'partial',
];

const statusOptions: SelectOption<
  never,
  Zod.infer<typeof ClaimResponseStatus>
>[] = statuses.map((status) => ({
  value: status,
  display: status,
}));

const outcomeOptions: SelectOption<
  never,
  Zod.infer<typeof ClaimResponseOutcome>
>[] = outcomes.map((outcome) => ({
  value: outcome,
  display: outcome,
}));

const searchFields: Field<SearchParams>[] = [
  {
    subfields: [
      {
        label: strings.LabelStatus,
        key: 'status',
        type: 'combobox-single',
        fetcher: statusOptions,
      },
      {
        label: strings.ClaimResponseOutcome,
        key: 'outcome',
        type: 'combobox-single',
        fetcher: outcomeOptions,
      },
    ],
  },
];

const getDataFromResponse = (res: ClaimResponseModelGetAllResponse) =>
  res.claimResponses;

export const ClaimResponseListScreen: React.FC = () => {
  const [search, setSearch] = useState<SearchParams>({});

  const fetcher = useCallback(
    (options?: FetcherOptions) =>
      api.getAll({ ...search, pageToken: options?.nextToken, ...options }),
    [search],
  );

  const onRowPress = useCallback(
    (row: WithId<Partial<ClaimResponseModelGetResponse>>) => {
      const id = row.id;
      if (!id) return;

      router.push(`/admin/claim/responses/${id}`);
    },
    [],
  );

  const baseSWRKey = `claim-response-list-${search.status}-${search.outcome}`;

  return (
    <ScrollView>
      <ClaimListToggle active="ListResponses">
        <CrudForm
          hasCard={false}
          fields={searchFields}
          initialValues={{}}
          onChange={setSearch}
        />
      </ClaimListToggle>
      <Section>
        <PaginatedTable
          cacheKey={baseSWRKey}
          fetcher={fetcher}
          getDataFromResponse={getDataFromResponse}
          columns={columns}
          canChangeCount
          onRowPress={onRowPress}
        />
      </Section>
    </ScrollView>
  );
};
